import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, MapPin, Building2, Mail, Download, Package, FileText, CalendarDays } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useT } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DistributorPortal() {
  const { user, logout } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [nextShow, setNextShow] = useState(null);

  useEffect(() => {
    fetch(`${API}/trade-shows`)
      .then((r) => (r.ok ? r.json() : []))
      .then((shows) => {
        // Pick the first upcoming show (starts_at ≥ now). Backend sorts ascending.
        const now = new Date().toISOString();
        const upcoming = (shows || []).find((s) => (s.starts_at || "") >= now) || shows?.[0];
        if (upcoming) setNextShow(upcoming);
      })
      .catch(() => {});
  }, []);

  const doLogout = () => { logout(); nav("/distributors/login", { replace: true }); };

  const downloadPortfolio = async () => {
    try {
      const token = localStorage.getItem("hupla_token");
      const res = await fetch(`${API}/distributor/portfolio`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Hupla-Brand-Portfolio-2026.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { /* silent — user can retry */ }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F7F5EE] text-[#0F1A12]" data-testid="distributor-portal">
      <header className="bg-[#0F1A12] text-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-4 flex items-center justify-between">
          <div>
            <p className="text-[0.65rem] font-bold uppercase tracking-[0.32em] text-[#B02224]">{t("dist.portalOverline")}</p>
            <h1 className="font-editorial italic text-2xl">{t("dist.welcome").replace("{name}", user.name)}</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-white/60">{user.email}</span>
            <button onClick={doLogout} data-testid="dist-logout" className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1.5 text-xs hover:bg-white/5">
              <LogOut className="w-3.5 h-3.5" /> {t("dist.signOut")}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
        {/* Territory card */}
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 p-8 mb-8" data-testid="dist-territory-card">
          <p className="text-[0.65rem] font-bold uppercase tracking-[0.32em] text-[#B02224] mb-4">{t("dist.yourTerritory")}</p>
          <div className="grid md:grid-cols-4 gap-6">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-black/50 mb-2"><Building2 className="w-3.5 h-3.5" /> {t("dist.colCompany")}</div>
              <p className="font-editorial italic text-xl">{user.company || "—"}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-black/50 mb-2"><MapPin className="w-3.5 h-3.5" /> {t("dist.colArea")}</div>
              <p className="font-editorial italic text-xl">{user.area || "—"}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-black/50 mb-2"><MapPin className="w-3.5 h-3.5" /> {t("dist.colCity")}</div>
              <p className="font-editorial italic text-xl">{user.city || "—"}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-black/50 mb-2"><MapPin className="w-3.5 h-3.5" /> {t("dist.colCountry")}</div>
              <p className="font-editorial italic text-xl">{user.country || "—"}</p>
            </div>
          </div>
        </div>

        <h2 className="font-editorial italic text-2xl mb-5">{t("dist.partnerResources")}</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {/* Card 1 — Portfolio PDF */}
          <button
            type="button"
            onClick={downloadPortfolio}
            data-testid="dist-resource-portfolio"
            className="group text-left bg-white rounded-2xl shadow-sm border border-black/5 p-6 hover:border-[#B02224]/40 transition-colors"
          >
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#0F1A12] text-white mb-4">
              <FileText className="w-5 h-5" />
            </div>
            <h3 className="font-semibold text-lg text-[#0F1A12] mb-1">{t("dist.res1Title")}</h3>
            <p className="text-sm text-black/65 leading-[1.6] mb-4">{t("dist.res1Body")}</p>
            <span className="text-sm font-semibold text-[#B02224] group-hover:underline">{t("dist.res1Action")} →</span>
          </button>

          {/* Card 2 — Samples */}
          <a
            href="/#b2b"
            data-testid="dist-resource-samples"
            className="group bg-white rounded-2xl shadow-sm border border-black/5 p-6 hover:border-[#B02224]/40 transition-colors"
          >
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#0F1A12] text-white mb-4">
              <Package className="w-5 h-5" />
            </div>
            <h3 className="font-semibold text-lg text-[#0F1A12] mb-1">{t("dist.res2Title")}</h3>
            <p className="text-sm text-black/65 leading-[1.6] mb-4">{t("dist.res2Body")}</p>
            <span className="text-sm font-semibold text-[#B02224] group-hover:underline">{t("dist.res2Action")} →</span>
          </a>

          {/* Card 3 — Next Trade Show (dynamic from DB) or fallback */}
          {nextShow ? (
            <a
              href="/#trade-shows"
              data-testid={`dist-resource-nextshow-${nextShow.id}`}
              className="group relative overflow-hidden bg-white rounded-2xl shadow-sm border border-black/5 p-6 hover:border-[#B02224]/40 transition-colors"
              style={{ borderTop: `4px solid ${nextShow.accent}` }}
            >
              <div
                className="inline-flex items-center justify-center w-11 h-11 rounded-full text-white mb-4"
                style={{ background: nextShow.accent }}
              >
                <CalendarDays className="w-5 h-5" />
              </div>
              <div
                className="text-[0.65rem] font-bold uppercase tracking-[0.24em] mb-1"
                style={{ color: nextShow.accent }}
              >
                {nextShow.date_label}
              </div>
              <h3 className="font-semibold text-lg text-[#0F1A12] mb-1">
                {t("dist.res3Title").replace(/\bshows\b/i, nextShow.name)}
              </h3>
              <p className="text-sm text-black/65 leading-[1.6] mb-4">
                {nextShow.venue} · {nextShow.city}, {nextShow.country}
              </p>
              <span className="text-sm font-semibold text-[#B02224] group-hover:underline">{t("dist.res3Action")} →</span>
            </a>
          ) : (
            <a
              href="/#trade-shows"
              data-testid="dist-resource-shows"
              className="group bg-white rounded-2xl shadow-sm border border-black/5 p-6 hover:border-[#B02224]/40 transition-colors"
            >
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#0F1A12] text-white mb-4">
                <Download className="w-5 h-5" />
              </div>
              <h3 className="font-semibold text-lg text-[#0F1A12] mb-1">{t("dist.res3Title")}</h3>
              <p className="text-sm text-black/65 leading-[1.6] mb-4">{t("dist.res3Body")}</p>
              <span className="text-sm font-semibold text-[#B02224] group-hover:underline">{t("dist.res3Action")} →</span>
            </a>
          )}
        </div>

        <p className="mt-10 text-xs text-black/40 flex items-center gap-2">
          <Mail className="w-3.5 h-3.5" /> {t("dist.helpMail")}
        </p>
      </main>
    </div>
  );
}
